import { Box, Tooltip } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import React from "react";

import ColorChip from "../../../components/ColorChip";
import { Artifact, CALL_SUITABILITY_STATUS_NOT_SCORED, formatContactDetails } from "../../../types/artifact";
import { CONVERSATION_LENS } from "../../../utils/terminology";

const MS_PER_SECOND = 1000;
const DURATION_DECIMAL_PLACES = 0;

const buildAttributeScoreColumn = (rankedAttribute: string): GridColDef => ({
  field: "attribute_score",
  headerName: "Attribute Score",
  width: 140,
  renderCell: (params: GridRenderCellParams): React.ReactElement => {
    const score = params.value as number;
    return (
      <Tooltip title={`View ${rankedAttribute} in Performance tab`} placement="top">
        <Box sx={{
          cursor: "pointer",
          ["&:hover"]: { opacity: 0.8 }
        }}>
          <ColorChip field="score" value={score} />
        </Box>
      </Tooltip>
    );
  }
});

const CORE_COLUMNS: GridColDef[] = [
  {
    field: "created",
    headerName: "Date",
    width: 150,
    valueGetter: (value: string): string => new Date(value).toLocaleString()
  },
  {
    field: "sc_overall_score",
    headerName: "Score",
    width: 65,
    renderCell: (params: GridRenderCellParams<Artifact>): React.ReactElement => {
      const score = params.value as number | null;
      const status = params.row.sc_call_suitability_status;
      const display = status === CALL_SUITABILITY_STATUS_NOT_SCORED ? null : score;
      return <ColorChip field="score" value={display} />;
    }
  },
  {
    field: "call_type",
    headerName: "Type",
    width: 50,
    align: "center",
    headerAlign: "center",
    renderCell: (params: GridRenderCellParams): React.ReactElement => {
      const callType = (params.value as string) || "external";
      return (
        <Tooltip title={callType === "internal" ? "Internal" : "External"} placement="top" arrow>
          <Box sx={{ display: "flex", width: "100%", height: "100%", justifyContent: "center", alignItems: "center" }}>
            <Box sx={{
              width: 24, height: 24, pt: "2px", borderRadius: "50%",
              backgroundColor: callType === "internal" ? "primary.main" : "success.main",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "white", fontWeight: "bold", fontSize: "12px"
            }}>
              {callType === "internal" ? "I" : "E"}
            </Box>
          </Box>
        </Tooltip>
      );
    }
  }
];

const INDICATOR_COLUMNS: GridColDef[] = [
  {
    field: "ra_overall_indicator", headerName: "Risk", width: 90, align: "center", headerAlign: "center",
    renderCell: (params: GridRenderCellParams): React.ReactElement => <ColorChip field="risk" value={params.value as string} />
  },
  {
    field: "relevance", headerName: "Relevance", width: 90, align: "center", headerAlign: "center",
    renderCell: (params: GridRenderCellParams): React.ReactElement => <ColorChip field="relevance" value={params.value as string} />
  },
  {
    field: "sentiment", headerName: "Sentiment", width: 90, align: "center", headerAlign: "center",
    renderCell: (params: GridRenderCellParams): React.ReactElement => <ColorChip field="sentiment" value={params.value as string} />
  }
];

const DATA_COLUMNS: GridColDef[] = [
  {
    field: "duration_ms_int", headerName: "Duration (s)", align: "right", headerAlign: "right", type: "number",
    valueFormatter: (value?: number): string => value ? (value / MS_PER_SECOND).toFixed(DURATION_DECIMAL_PLACES) : ""
  },
  {
    field: "contact_number", headerName: "Contact", width: 250,
    valueGetter: (_value: string, row: Artifact): string =>
      formatContactDetails({
        firstName: row.contact_first_name, lastName: row.contact_last_name,
        companyName: row.contact_company_name, contactNumber: row.contact_number
      })
  },
  { field: "user_email", headerName: "User Email", width: 250 },
  { field: "intent", headerName: "Intent", width: 380 },
  { field: "sc_name", headerName: CONVERSATION_LENS, width: 250 },
  { field: "ra_process_decision", headerName: "RA Process Decision" },
  { field: "summary", headerName: "Summary" },
  { field: "category", headerName: "Category", align: "center", headerAlign: "center" },
  { field: "sc_summary", headerName: "Scorecard Summary" },
  { field: "pp_process_decision", headerName: "Processed" },
  { field: "pp_reason_code", headerName: "Reason Code", width: 310 },
  { field: "pp_detailed_reasoning", headerName: "Detailed Reasoning", width: 550 }
];

export const buildColumns = (
  isRankedActive: boolean,
  rankedAttribute?: string
): GridColDef[] => [
  ...(isRankedActive && rankedAttribute ? [buildAttributeScoreColumn(rankedAttribute)] : []),
  ...CORE_COLUMNS,
  ...INDICATOR_COLUMNS,
  ...DATA_COLUMNS
];
